import { computed } from 'vue';
import { NavBarBgColor } from '../../../components/layout/NavBar/model.js';
import { draftWaypointDetailsRoute } from '../locations.js';
import { SchedulingFlowRouteName } from './model.js';
import { schedulingFlowHeadingWithNav } from './privateHelpers.js';
import { getStore } from '../../../store/helpers/initStore.js';
import { isExactTheme } from '../../../store/modules/app/helpers.js';
import { BrandTheme } from '../../../store/modules/app/model.js';

// Paths below are not full paths - they are based on the parent path:
// /:partnerName?/schedule/:draftWaypointID?
const routesSchedulingFlowChildren = [
    {
        path: 'visit',
        name: SchedulingFlowRouteName.SCHEDULING_FLOW_ADDRESS,
        props: true,
        component: () => import('../../../components/specific/schedulingFlow/SchedulingFlow/steps/root/SchedulingFlowAddress.vue.js'),
        meta: {
            allowIncomplete: true,
            noPortal: true,
            heading: (headingParams) => {
                const { isNurseRescheduling } = headingParams.store.state.schedulingFlow;
                return schedulingFlowHeadingWithNav({
                    title: {
                        [BrandTheme.CARESEND]: isNurseRescheduling ? undefined : 'Schedule visit',
                        [BrandTheme.EXACT]: undefined,
                    },
                })(headingParams);
            },
            /** When accessing without authentication, navbar should remain white */
            navBarColorOverride: NavBarBgColor.WHITE,
            whiteBackground: true,
        },
    },
    {
        path: 'flexibleOption',
        name: SchedulingFlowRouteName.SCHEDULING_FLOW_FLEXIBLE_OPTION,
        props: true,
        component: () => import('../../../components/specific/schedulingFlow/SchedulingFlow/steps/root/SchedulingFlowFlexibleOption.vue.js'),
        meta: {
            allowIncomplete: true,
            noPortal: true,
            heading: schedulingFlowHeadingWithNav({
                title: 'Select an option',
            }),
            /** When accessing without authentication, navbar should remain white */
            navBarColorOverride: NavBarBgColor.WHITE,
            whiteBackground: true,
        },
    },
    {
        path: 'dateRange',
        name: SchedulingFlowRouteName.SCHEDULING_FLOW_DATE_RANGE,
        props: true,
        component: () => import('../../../components/specific/schedulingFlow/SchedulingFlow/steps/root/SchedulingFlowDateRange.vue.js'),
        meta: {
            allowIncomplete: true,
            noPortal: true,
            heading: schedulingFlowHeadingWithNav({
                title: 'Schedule visit',
            }),
            /** When accessing without authentication, navbar should remain white */
            navBarColorOverride: NavBarBgColor.WHITE,
            whiteBackground: true,
        },
    },
    {
        path: 'timeRange',
        name: SchedulingFlowRouteName.SCHEDULING_FLOW_TIME_RANGE,
        props: true,
        component: () => import('../../../components/specific/schedulingFlow/SchedulingFlow/steps/root/SchedulingFlowTimeRange.vue.js'),
        meta: {
            allowIncomplete: true,
            noPortal: true,
            heading: schedulingFlowHeadingWithNav({
                title: 'Schedule visit',
            }),
            /** When accessing without authentication, navbar should remain white */
            navBarColorOverride: NavBarBgColor.WHITE,
            whiteBackground: true,
        },
    },
    {
        path: 'time/:visitType?',
        name: SchedulingFlowRouteName.SCHEDULING_FLOW_TIME,
        props: true,
        component: () => import('../../../components/specific/schedulingFlow/SchedulingFlow/steps/root/SchedulingFlowTime.vue.js'),
        meta: {
            allowIncomplete: true,
            noPortal: true,
            heading: (headingParams) => {
                const { isNurseRescheduling } = headingParams.store.state.schedulingFlow;
                return schedulingFlowHeadingWithNav({
                    title: {
                        [BrandTheme.CARESEND]: isNurseRescheduling ? undefined : 'Schedule visit',
                        [BrandTheme.EXACT]: undefined,
                    },
                    withStickyHeader: true,
                })(headingParams);
            },
            /** When accessing without authentication, navbar should remain white */
            navBarColorOverride: NavBarBgColor.WHITE,
            whiteBackground: true,
        },
    },
    {
        path: 'walk-in-date',
        name: SchedulingFlowRouteName.SCHEDULING_FLOW_WALK_IN_DATE,
        props: true,
        component: () => import('../../../components/specific/schedulingFlow/SchedulingFlow/steps/root/SchedulingFlowWalkInDate.vue.js'),
        meta: {
            allowIncomplete: true,
            noPortal: true,
            heading: schedulingFlowHeadingWithNav({
                backButtonText: 'Back to locations',
            }),
            /** When accessing without authentication, navbar should remain white */
            navBarColorOverride: NavBarBgColor.WHITE,
            whiteBackground: true,
        },
    },
    {
        path: 'visit-overview',
        /** should the route name match the component name? */
        name: SchedulingFlowRouteName.SCHEDULING_FLOW_VISIT_OVERVIEW,
        props: true,
        component: () => import('../../../components/specific/schedulingFlow/SchedulingFlow/steps/root/SchedulingFlowCheckout.vue.js'),
        meta: {
            allowIncomplete: true,
            noPortal: true,
            authRequired: true,
            authSubMeta: {
                continueFlow: true,
                deferSignup: true,
            },
            /**
             * This step is a little different because we display a confirm button
             * in the heading during normal scheduling. If we are rescheduling or the
             * visit is not free, we display both the confirm and discard changes buttons
             * together below the content of the page, rather than in the heading.
             */
            heading: (headingParams) => schedulingFlowHeadingWithNav({ hideBackButton: isExactTheme() })(headingParams),
            /** When accessing without authentication, navbar should remain white */
            navBarColorOverride: NavBarBgColor.WHITE,
            whiteBackground: true,
        },
    },
    {
        path: 'review',
        name: SchedulingFlowRouteName.SCHEDULING_FLOW_REVIEW,
        props: true,
        component: () => import('../../../components/specific/schedulingFlow/SchedulingFlow/steps/root/SchedulingFlowReview.vue.js'),
        meta: {
            allowIncomplete: true,
            noPortal: true,
            heading: (headingParams) => {
                var _a;
                const { route } = headingParams;
                const draftWaypointID = (_a = route.params.draftWaypointID) !== null && _a !== void 0 ? _a : null;
                return schedulingFlowHeadingWithNav({
                    confirmToOverride: draftWaypointDetailsRoute({ params: { draftWaypointID } }),
                    hideBackButton: true,
                    withGlobalFooter: true,
                })(headingParams);
            },
            /** When accessing without authentication, navbar should remain white */
            navBarColorOverride: NavBarBgColor.WHITE,
            whiteBackground: true,
        },
    },
    {
        path: 'complete-profile',
        name: SchedulingFlowRouteName.SCHEDULING_FLOW_COMPLETE_PROFILE,
        props: true,
        component: () => import('../../../components/specific/schedulingFlow/SchedulingFlow/steps/root/SchedulingFlowCompleteProfile.vue.js'),
        meta: {
            allowIncomplete: true,
            noPortal: true,
            heading: (headingParams) => {
                const { isNurseRescheduling } = headingParams.store.state.schedulingFlow;
                return schedulingFlowHeadingWithNav({
                    title: {
                        [BrandTheme.CARESEND]: isNurseRescheduling ? undefined : 'Your information',
                        [BrandTheme.EXACT]: 'Your information',
                    },
                })(headingParams);
            },
            /** When accessing without authentication, navbar should remain white */
            navBarColorOverride: NavBarBgColor.WHITE,
            whiteBackground: true,
        },
    },
    {
        path: 'practice-info',
        name: SchedulingFlowRouteName.SCHEDULING_FLOW_PRACTICE_INFO,
        props: true,
        component: () => import('../../../components/specific/schedulingFlow/SchedulingFlow/steps/root/SchedulingFlowPracticeInfo.vue.js'),
        meta: {
            allowIncomplete: true,
            noPortal: true,
            heading: (headingParams) => {
                const { isNurseRescheduling } = headingParams.store.state.schedulingFlow;
                return schedulingFlowHeadingWithNav({
                    title: {
                        [BrandTheme.CARESEND]: isNurseRescheduling ? undefined : 'Your practice',
                        [BrandTheme.EXACT]: 'Your practice',
                    },
                })(headingParams);
            },
            /** When accessing without authentication, navbar should remain white */
            navBarColorOverride: NavBarBgColor.WHITE,
            whiteBackground: true,
        },
    },
    {
        path: 'rescheduling-job',
        name: SchedulingFlowRouteName.SCHEDULING_FLOW_RESCHEDULE_LOADING,
        props: true,
        component: () => import('../../../components/specific/schedulingFlow/SchedulingFlow/steps/root/SchedulingFlowRescheduleLoading.vue.js'),
        meta: {
            allowIncomplete: true,
            noPortal: true,
            whiteBackground: true,
        },
    },
];
const routesSchedulingFlow = [
    {
        path: '/:partnerName?/schedule/:draftWaypointID?',
        name: SchedulingFlowRouteName.SCHEDULING_FLOW,
        props: true,
        component: () => import('../../../components/specific/schedulingFlow/SchedulingFlow/SchedulingFlow.vue.js'),
        children: routesSchedulingFlowChildren,
        meta: {
            allowIncomplete: true,
            /** When accessing without authentication, navbar should remain white */
            navBarColorOverride: NavBarBgColor.WHITE,
            noPortal: true,
            loadingIdTrackingEnabled: true,
            loadUntilTrue: () => computed(() => !getStore().state.app.loadingIDs[SchedulingFlowRouteName.SCHEDULING_FLOW]),
            loadingMessage: () => 'Retrieving order…',
            whiteBackground: true,
        },
    },
];
/**
 * RootState must be passed as type param.
 *
 * Example: `getRoutesSchedulingFlow<RootState>()`
 *
 * RootState is type-checked to ensure the routes cannot be added to an app that
 * does not have the necessary vuex modules set up.
 */
const getRoutesSchedulingFlow = () => routesSchedulingFlow;
/**
 * Returns a guard that redirects to the same route with `continueFlow` query param
 * as to not break the flow when the user navigates back to the scheduling flow from
 * a different route defined on the app.
 *
 * Currently used exclusively to cover navigation from the login page to the scheduling
 * flow.
 */
const getRedirectGuardSchedulingFlow = (expectedFromRouteName) => (_to, from, next) => {
    var _a;
    const to = _to;
    const schedulingFlowRouteNames = Object.values(SchedulingFlowRouteName);
    const toName = (_a = to.name) !== null && _a !== void 0 ? _a : null;
    // The initial route of the scheduling flow should not use continueFlow, as
    // it represents the first navigation to the scheduling flow, and the full
    // initialization is required.
    const isToSchedulingFlowInit = to.name
        === SchedulingFlowRouteName.SCHEDULING_FLOW;
    if (from.name === expectedFromRouteName
        && !to.query.continueFlow
        && schedulingFlowRouteNames.includes(toName)
        && !isToSchedulingFlowInit) {
        to.query.continueFlow = String(true);
        next({
            path: to.path,
            query: to.query,
        });
    }
    else {
        next();
    }
};

export { getRedirectGuardSchedulingFlow, getRoutesSchedulingFlow };
